// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'antd/lib/grid';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Text from 'antd/lib/typography/Text';
import InputNumber from 'antd/lib/input-number';
import Select from 'antd/lib/select';
import Slider from 'antd/lib/slider';

import {
    DEFAULT_ASINH_SOFTNESS, DEFAULT_GAMMA, DisplayCurve, autoDisplay, currentGray16, fullRange,
} from 'utils/sixteen-bit';
import type { Gray16 } from 'utils/sixteen-bit';
import Button from 'antd/lib/button';

import {
    switchGrid,
    changeGridColor,
    changeGridOpacity,
    changeBrightnessLevel,
    changeContrastLevel,
    changeMinLevel,
    changeMaxLevel,
    changeGridSize,
    resetImageFilters,
    changeDisplayCurve,
    changeCurveParameter,
    setDisplayWindow,
} from 'actions/settings-actions';
import { clamp } from 'utils/math';
import { GridColor, CombinedState, PlayerSettingsState } from 'reducers';

import ImageHistogram from './image-histogram';

const minGridSize = 5;
const maxGridSize = 1000;

// The asinh softness spans four decades, so its slider moves in log10 units.
const minSoftnessLog = -3;
const maxSoftnessLog = 1;

/**
 * The 8-bit background canvas as greyscale samples, for Auto on an ordinary frame. Read back
 * BEFORE the CSS filter (getImageData returns the buffer), so it measures the unwindowed frame.
 */
function canvasAsGray(canvasInstance: unknown): Gray16 | null {
    const wrapper = (canvasInstance && typeof (canvasInstance as any).html === 'function' ?
        (canvasInstance as any).html() : null) as HTMLElement | null;
    const background = (wrapper?.querySelector('canvas#cvat_canvas_background') ??
        wrapper?.querySelector('canvas')) as HTMLCanvasElement | null;
    if (!background || !background.width || !background.height) return null;
    const context = background.getContext('2d');
    if (!context) return null;
    let pixels: Uint8ClampedArray;
    try {
        pixels = context.getImageData(0, 0, background.width, background.height).data;
    } catch {
        return null;
    }
    const data = new Uint16Array(pixels.length / 4);
    let max = 0;
    for (let i = 0, j = 0; i < data.length; i++, j += 4) {
        const value = Math.round(pixels[j] * 0.299 + pixels[j + 1] * 0.587 + pixels[j + 2] * 0.114);
        data[i] = value;
        if (value > max) max = value;
    }
    return {
        width: background.width, height: background.height, data, max,
    };
}

export default function ImageSetupsContent(): JSX.Element {
    const dispatch = useDispatch();
    const {
        brightnessLevel,
        contrastLevel,
        minLevel,
        maxLevel,
        // 255 on an ordinary frame, the camera's bit depth on a 16-bit one, so the sliders
        // address camera counts instead of a rescaled copy of them. Comes through the store so
        // that the panel re-renders when the viewer reports a frame of a different depth.
        levelLimit,
        displayCurve,
        gammaLevel,
        asinhSoftness,
        gridOpacity,
        gridColor,
        gridSize,
        grid: gridEnabled,
    } = useSelector((state: CombinedState): PlayerSettingsState => state.settings.player);
    const canvasInstance = useSelector((state: CombinedState) => state.annotation.canvas.instance);

    const curveParameter = displayCurve === 'gamma' ? gammaLevel : asinhSoftness;

    // Auto for whichever curve is selected. Linear is ImageJ's Enhance Contrast (saturated=0.35)
    // rather than its Auto button, which burns the cell body white on our frames. Gamma and asinh
    // fit the whole range and solve the curve so the background lands on a dark grey, as the
    // server's asinh preview does. One fixed state: pressing Auto again changes nothing.
    const onAuto = useCallback((): void => {
        // On a 16-bit frame the canvas holds the already-windowed 8-bit result, so measuring it
        // would produce a 0..255 window for a 0..2020 scale -- a black image. Measure the samples.
        const source = currentGray16() ?? canvasAsGray(canvasInstance);
        if (!source) return;
        const { min, max, parameter } = autoDisplay(source, displayCurve, curveParameter);
        dispatch(setDisplayWindow(min, max, parameter));
    }, [canvasInstance, dispatch, displayCurve, curveParameter]);

    // ImageJ's Reset, not a reset to the stored defaults: the display range goes to the FULL data
    // range, so nothing is clipped at either end and every value the frame carries is on screen.
    // On a 16-bit frame the old behaviour, a hard 0..255, was the worst window available:
    // everything from 255 up collapsed to white. The selected curve stays, at its neutral setting.
    const onReset = useCallback((): void => {
        const source = currentGray16();
        const { min, max } = source ? fullRange(source) : { min: 0, max: 255 };
        dispatch(setDisplayWindow(min, max, displayCurve === 'gamma' ? DEFAULT_GAMMA : DEFAULT_ASINH_SOFTNESS));
        dispatch(resetImageFilters());
    }, [dispatch, displayCurve]);

    return (
        <div className='cvat-canvas-image-setups-content'>
            <Text>Image grid</Text>
            <hr />
            <Row justify='space-between' align='middle' gutter={8}>
                <Col span={1} />
                <Col span={6}>
                    <Text className='cvat-text-color'> Size </Text>
                </Col>
                <Col span={8}>
                    <Text className='cvat-text-color'> Color </Text>
                </Col>
                <Col span={8}>
                    <Text className='cvat-text-color'> Opacity </Text>
                </Col>
            </Row>
            <Row justify='space-between' align='middle' gutter={8}>
                <Col span={1}>
                    <Checkbox
                        className='cvat-text-color cvat-image-setups-grid'
                        checked={gridEnabled}
                        onChange={(event: CheckboxChangeEvent): void => {
                            dispatch(switchGrid(event.target.checked));
                        }}
                    />
                </Col>
                <Col span={6} className='cvat-image-setups-grid-size'>
                    <InputNumber
                        className='cvat-image-setups-grid-size-input'
                        min={minGridSize}
                        max={maxGridSize}
                        value={gridSize}
                        disabled={!gridEnabled}
                        onChange={(value: number | undefined | null | string): void => {
                            if (typeof value !== 'undefined' && value !== null) {
                                const converted = Math.floor(clamp(+value, minGridSize, maxGridSize));
                                dispatch(changeGridSize(converted));
                            }
                        }}
                    />
                </Col>
                <Col span={8} className='cvat-image-setups-grid-color'>
                    <Select
                        className='cvat-image-setups-grid-color-input'
                        value={gridColor}
                        disabled={!gridEnabled}
                        onChange={(color: GridColor): void => {
                            dispatch(changeGridColor(color));
                        }}
                    >
                        <Select.Option key='white' value={GridColor.White}>
                            White
                        </Select.Option>
                        <Select.Option key='black' value={GridColor.Black}>
                            Black
                        </Select.Option>
                        <Select.Option key='red' value={GridColor.Red}>
                            Red
                        </Select.Option>
                        <Select.Option key='green' value={GridColor.Green}>
                            Green
                        </Select.Option>
                        <Select.Option key='blue' value={GridColor.Blue}>
                            Blue
                        </Select.Option>
                    </Select>
                </Col>
                <Col span={8} className='cvat-image-setups-grid-opacity'>
                    <Slider
                        className='cvat-image-setups-grid-opacity-input'
                        min={0}
                        max={100}
                        value={gridOpacity}
                        disabled={!gridEnabled}
                        onChange={(value: number | [number, number]): void => {
                            dispatch(changeGridOpacity(value as number));
                        }}
                    />
                </Col>
            </Row>
            <Text>Color settings</Text>
            <hr />
            <ImageHistogram
                minLevel={minLevel}
                maxLevel={maxLevel}
                levelLimit={levelLimit}
                curve={displayCurve}
                curveParameter={curveParameter}
            />
            <Row justify='space-around'>
                <Col span={24}>
                    <Row className='cvat-image-setups-brightness' align='middle' gutter={8}>
                        <Col span={5}>
                            <Text className='cvat-text-color'> Brightness </Text>
                        </Col>
                        <Col span={13}>
                            <Slider
                                min={0}
                                max={levelLimit}
                                step={1}
                                value={Math.round(brightnessLevel)}
                                onChange={(value: number | [number, number]): void => {
                                    dispatch(changeBrightnessLevel(value as number));
                                }}
                            />
                        </Col>
                        <Col span={6}>
                            <InputNumber
                                size='small'
                                min={0}
                                max={levelLimit}
                                step={1}
                                value={Math.round(brightnessLevel)}
                                onChange={(value: number | undefined | null | string): void => {
                                    if (typeof value === 'number') {
                                        dispatch(changeBrightnessLevel(clamp(value, 0, levelLimit)));
                                    }
                                }}
                            />
                        </Col>
                    </Row>
                    <Row className='cvat-image-setups-contrast' align='middle' gutter={8}>
                        <Col span={5}>
                            <Text className='cvat-text-color'> Contrast </Text>
                        </Col>
                        <Col span={13}>
                            <Slider
                                min={0}
                                max={levelLimit - 1}
                                step={1}
                                value={Math.round(contrastLevel)}
                                onChange={(value: number | [number, number]): void => {
                                    dispatch(changeContrastLevel(value as number));
                                }}
                            />
                        </Col>
                        <Col span={6}>
                            <InputNumber
                                size='small'
                                min={0}
                                max={levelLimit - 1}
                                step={1}
                                value={Math.round(contrastLevel)}
                                onChange={(value: number | undefined | null | string): void => {
                                    if (typeof value === 'number') {
                                        dispatch(changeContrastLevel(clamp(value, 0, levelLimit - 1)));
                                    }
                                }}
                            />
                        </Col>
                    </Row>
                    <Row className='cvat-image-setups-min' align='middle' gutter={8}>
                        <Col span={5}>
                            <Text className='cvat-text-color'> Min </Text>
                        </Col>
                        <Col span={13}>
                            <Slider
                                min={0}
                                max={levelLimit}
                                step={1}
                                value={Math.round(minLevel)}
                                onChange={(value: number | [number, number]): void => {
                                    dispatch(changeMinLevel(value as number));
                                }}
                            />
                        </Col>
                        <Col span={6}>
                            <InputNumber
                                size='small'
                                min={0}
                                max={levelLimit}
                                step={1}
                                value={Math.round(minLevel)}
                                onChange={(value: number | undefined | null | string): void => {
                                    if (typeof value === 'number') {
                                        dispatch(changeMinLevel(clamp(value, 0, levelLimit)));
                                    }
                                }}
                            />
                        </Col>
                    </Row>
                    <Row className='cvat-image-setups-max' align='middle' gutter={8}>
                        <Col span={5}>
                            <Text className='cvat-text-color'> Max </Text>
                        </Col>
                        <Col span={13}>
                            <Slider
                                min={0}
                                max={levelLimit}
                                step={1}
                                value={Math.round(maxLevel)}
                                onChange={(value: number | [number, number]): void => {
                                    dispatch(changeMaxLevel(value as number));
                                }}
                            />
                        </Col>
                        <Col span={6}>
                            <InputNumber
                                size='small'
                                min={0}
                                max={levelLimit}
                                step={1}
                                value={Math.round(maxLevel)}
                                onChange={(value: number | undefined | null | string): void => {
                                    if (typeof value === 'number') {
                                        dispatch(changeMaxLevel(clamp(value, 0, levelLimit)));
                                    }
                                }}
                            />
                        </Col>
                    </Row>
                    <Row className='cvat-image-setups-curve' align='middle' gutter={8}>
                        <Col span={5}>
                            <Text className='cvat-text-color'> Curve </Text>
                        </Col>
                        <Col span={19}>
                            <Select
                                size='small'
                                className='cvat-image-setups-curve-select'
                                value={displayCurve}
                                onChange={(value: DisplayCurve): void => {
                                    dispatch(changeDisplayCurve(value));
                                }}
                            >
                                <Select.Option value='linear'>Linear (ImageJ)</Select.Option>
                                <Select.Option value='gamma'>Gamma</Select.Option>
                                <Select.Option value='asinh'>Asinh</Select.Option>
                            </Select>
                        </Col>
                    </Row>
                    {displayCurve === 'gamma' && (
                        <Row className='cvat-image-setups-gamma' align='middle' gutter={8}>
                            <Col span={5}>
                                <Text className='cvat-text-color'> Gamma </Text>
                            </Col>
                            <Col span={13}>
                                <Slider
                                    min={0.1}
                                    max={3}
                                    step={0.01}
                                    value={gammaLevel}
                                    onChange={(value: number | [number, number]): void => {
                                        dispatch(changeCurveParameter(value as number));
                                    }}
                                />
                            </Col>
                            <Col span={6}>
                                <InputNumber
                                    size='small'
                                    min={0.05}
                                    max={5}
                                    step={0.01}
                                    precision={2}
                                    value={gammaLevel}
                                    onChange={(value: number | undefined | null | string): void => {
                                        if (typeof value === 'number') {
                                            dispatch(changeCurveParameter(clamp(value, 0.05, 5)));
                                        }
                                    }}
                                />
                            </Col>
                        </Row>
                    )}
                    {displayCurve === 'asinh' && (
                        <Row className='cvat-image-setups-asinh' align='middle' gutter={8}>
                            <Col span={5}>
                                <Text className='cvat-text-color'> Softness </Text>
                            </Col>
                            <Col span={13}>
                                <Slider
                                    min={minSoftnessLog}
                                    max={maxSoftnessLog}
                                    step={0.01}
                                    value={Math.log10(asinhSoftness)}
                                    tipFormatter={(value?: number): string => (
                                        typeof value === 'number' ? (10 ** value).toPrecision(2) : ''
                                    )}
                                    onChange={(value: number | [number, number]): void => {
                                        dispatch(changeCurveParameter(10 ** (value as number)));
                                    }}
                                />
                            </Col>
                            <Col span={6}>
                                <InputNumber
                                    size='small'
                                    min={10 ** minSoftnessLog}
                                    max={10 ** maxSoftnessLog}
                                    step={0.001}
                                    precision={4}
                                    value={asinhSoftness}
                                    onChange={(value: number | undefined | null | string): void => {
                                        if (typeof value === 'number' && value > 0) {
                                            dispatch(changeCurveParameter(
                                                clamp(value, 10 ** minSoftnessLog, 10 ** maxSoftnessLog),
                                            ));
                                        }
                                    }}
                                />
                            </Col>
                        </Row>
                    )}
                </Col>
            </Row>
            <Row className='cvat-image-setups-reset-color-settings' justify='space-around' gutter={8}>
                <Col>
                    <Button
                        className='cvat-image-setups-auto-button'
                        onClick={onAuto}
                    >
                        Auto
                    </Button>
                </Col>
                <Col>
                    <Button
                        className='cvat-image-setups-reset-color-settings-button'
                        onClick={onReset}
                    >
                        Reset color settings
                    </Button>
                </Col>
            </Row>
        </div>
    );
}
